import React, { useMemo } from "react";
import { Button } from "reactstrap";
import { DataTable } from "../../../components/Common";
import { MEMBER_STATUS_LABELS } from "../../../constants/members";
import { GENDER_LABELS } from "../../../constants/gender";
import * as XLSX from "xlsx";
import neoLogo from "../../../assets/images/Neo_Logo_A.svg";

/**
 * CrmMemberList
 * Tabela de membros filtrados usando o DataTable padrão do projeto.
 * Inclui ações de Exportar Excel e Imprimir nos botões do header.
 * Exibe resultados somente após clicar em "Buscar".
 */
const CrmMemberList = ({ members, loading, hasSearched, onSelectMember, tenantName, tenantLogo }) => {

    // ── Colunas do DataTable ──
    const columns = useMemo(() => [
        {
            label: "#",
            key: "index",
            style: { width: 40 },
            className: "text-muted font-size-12",
            render: (_row, _idx, { page, rowsPerPage }) => (
                <span>{(page - 1) * rowsPerPage + _idx + 1}</span>
            ),
        },
        {
            label: "Nome",
            key: "fullName",
            render: (row) => (
                <div
                    className="d-flex align-items-center cursor-pointer"
                    onClick={() => onSelectMember && onSelectMember(row)}
                >
                    <div className="avatar-xs flex-shrink-0 me-2">
                        {row.photoUrl ? (
                            <img
                                src={row.photoUrl}
                                alt={row.fullName}
                                className="img-fluid rounded-circle w-100 h-100"
                                style={{ objectFit: "cover" }}
                            />
                        ) : (
                            <span className="avatar-title rounded-circle bg-light text-muted">
                                <i className="mdi mdi-account-outline"></i>
                            </span>
                        )}
                    </div>
                    <span className="fw-semibold text-dark text-truncate" style={{ fontSize: "0.85rem", maxWidth: 180 }}>
                        {row.fullName || "Sem Nome"}
                    </span>
                </div>
            ),
        },
        {
            label: "Email",
            key: "email",
            className: "text-muted",
            cellStyle: { fontSize: "0.82rem", maxWidth: 180 },
            render: (row) => (
                <span className="text-truncate d-inline-block" style={{ maxWidth: 180 }}>
                    {row.email || "—"}
                </span>
            ),
        },
        {
            label: "Telefone",
            key: "phone",
            className: "text-muted",
            cellStyle: { fontSize: "0.82rem" },
            render: (row) => formatPhone(row.phone) || "—",
        },
        {
            label: "Sexo",
            key: "gender",
            headerClassName: "text-center",
            className: "text-center text-muted",
            cellStyle: { fontSize: "0.82rem" },
            render: (row) => GENDER_LABELS[row.gender] || "—",
        },
        {
            label: "Status",
            key: "status",
            headerClassName: "text-center",
            className: "text-center",
            render: (row) => {
                const badge = getStatusBadge(row.status);
                return (
                    <span className={`badge ${badge.className} font-size-10 fw-medium px-2 py-1 rounded-pill`}>
                        {badge.label}
                    </span>
                );
            },
        },
        {
            label: "Idade",
            key: "age",
            headerClassName: "text-center",
            className: "text-center text-muted",
            cellStyle: { fontSize: "0.82rem" },
            render: (row) => {
                const age = row.birthDate ? calculateAge(row.birthDate) : null;
                return age !== null ? age : "—";
            },
        },
        {
            label: "Atividades",
            key: "activityNames",
            cellStyle: { fontSize: "0.82rem" },
            render: (row) => {
                const acts = row.activityNames || [];
                if (acts.length === 0) return "—";
                return (
                    <div className="d-flex flex-wrap gap-1" style={{ maxWidth: 200 }}>
                        {acts.slice(0, 2).map((act, i) => (
                            <span key={i} className="badge bg-primary bg-opacity-10 text-primary font-size-10 fw-normal px-2 py-1 rounded-pill">
                                {act}
                            </span>
                        ))}
                        {acts.length > 2 && (
                            <span className="badge bg-light text-muted font-size-10 fw-normal px-2 py-1 rounded-pill">
                                +{acts.length - 2}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            label: "Consultor",
            key: "consultant",
            className: "text-muted",
            cellStyle: { fontSize: "0.82rem", maxWidth: 120 },
            render: (row) => (
                <span className="text-truncate d-inline-block" style={{ maxWidth: 120 }}>
                    {row.consultant?.name || "—"}
                </span>
            ),
        },
    ], [onSelectMember]);

    // ── Exportar Excel ──
    const handleExportExcel = () => {
        if (!members || members.length === 0) return;

        const data = members.map(m => ({
            "Nome": m.fullName || "",
            "Email": m.email || "",
            "Telefone": formatPhone(m.phone) || "",
            "CPF": m.document || "",
            "Sexo": GENDER_LABELS[m.gender] || "",
            "Status": MEMBER_STATUS_LABELS[m.status] || m.status || "",
            "Idade": m.birthDate ? calculateAge(m.birthDate) : "",
            "Atividades": (m.activityNames || []).join(", "),
            "Consultor": m.consultant?.name || "",
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "CRM");

        // Auto-width columns
        const colWidths = Object.keys(data[0]).map(key => ({
            wch: Math.max(key.length, ...data.map(row => String(row[key] || "").length)) + 2
        }));
        ws["!cols"] = colWidths;

        XLSX.writeFile(wb, `CRM_Clientes_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // ── Imprimir (estilo PrintableWorkout) ──
    const handlePrint = () => {
        if (!members || members.length === 0) return;

        const rows = members.map((m, i) =>
            `<tr style="border-bottom: 1px solid #dee2e6; background-color: ${i % 2 === 0 ? "#fff" : "#f8f9fa"};">
                <td style="padding: 8px 12px; text-align: center; color: #555;">${i + 1}</td>
                <td style="padding: 8px 12px; font-weight: 500; color: #111;">${m.fullName || ""}</td>
                <td style="padding: 8px 12px; color: #555;">${m.email || ""}</td>
                <td style="padding: 8px 12px; color: #555;">${formatPhone(m.phone) || ""}</td>
                <td style="padding: 8px 12px; text-align: center; color: #555;">${GENDER_LABELS[m.gender] || ""}</td>
                <td style="padding: 8px 12px; text-align: center; font-weight: 600; color: #333;">${MEMBER_STATUS_LABELS[m.status] || m.status || ""}</td>
                <td style="padding: 8px 12px; text-align: center; color: #555;">${m.birthDate ? calculateAge(m.birthDate) : ""}</td>
                <td style="padding: 8px 12px; color: #555; font-size: 12px;">${(m.activityNames || []).join(", ")}</td>
                <td style="padding: 8px 12px; color: #555;">${m.consultant?.name || ""}</td>
            </tr>`
        ).join("");

        const now = new Date();
        const dateStr = now.toLocaleDateString("pt-BR");
        const timeStr = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

        const printWindow = window.open("", "_blank");
        printWindow.document.write(`
            <html>
            <head>
                <title>CRM - Lista de Clientes</title>
                <style>
                    @media print { body { margin: 10px; } }
                </style>
            </head>
            <body style="padding: 30px 40px; font-family: 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #333; background: #fff;">

                <!-- CABEÇALHO -->
                <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #eee; padding-bottom: 20px; margin-bottom: 24px;">
                    <div style="flex: 0 0 auto; min-width: 120px;">
                        ${tenantLogo ? `<img src="${tenantLogo}" alt="Logo" style="max-height: 70px; max-width: 160px; object-fit: contain;" />` : ""}
                    </div>
                    <div style="text-align: right; flex: 1 1 auto; padding-left: 15px;">
                        <h2 style="margin: 0 0 6px 0; font-size: 14px; font-weight: 700; color: #555; text-transform: uppercase; letter-spacing: 0.5px;">
                            ${tenantName || ""}
                        </h2>
                        <h3 style="margin: 0 0 4px 0; font-size: 18px; font-weight: 700; color: #111;">
                            CRM - Lista de Clientes
                        </h3>
                        <p style="margin: 2px 0; font-size: 13px; color: #555;">
                            <strong>Total:</strong> ${members.length} cliente(s)
                        </p>
                        <p style="margin: 2px 0; font-size: 13px; color: #555;">
                            <strong>Data:</strong> ${dateStr}
                        </p>
                    </div>
                </div>

                <!-- TABELA -->
                <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                    <thead>
                        <tr style="background-color: #212529; color: #fff; text-align: left;">
                            <th style="padding: 8px 12px; font-weight: 600; width: 5%; text-align: center;">#</th>
                            <th style="padding: 8px 12px; font-weight: 600; width: 18%;">Nome</th>
                            <th style="padding: 8px 12px; font-weight: 600; width: 18%;">Email</th>
                            <th style="padding: 8px 12px; font-weight: 600; width: 12%;">Telefone</th>
                            <th style="padding: 8px 12px; font-weight: 600; width: 7%; text-align: center;">Sexo</th>
                            <th style="padding: 8px 12px; font-weight: 600; width: 10%; text-align: center;">Status</th>
                            <th style="padding: 8px 12px; font-weight: 600; width: 7%; text-align: center;">Idade</th>
                            <th style="padding: 8px 12px; font-weight: 600; width: 15%;">Atividades</th>
                            <th style="padding: 8px 12px; font-weight: 600; width: 10%;">Consultor</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>

                <!-- RODAPÉ -->
                <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #eee;">
                    <div style="display: flex; justify-content: flex-end; align-items: flex-end;">
                        <div style="text-align: right;">
                            <p style="margin: 0 0 5px 0; font-size: 11px; font-style: italic; color: #999;">
                                Gerado em ${dateStr} às ${timeStr}.
                            </p>
                            <div style="display: flex; align-items: center; justify-content: flex-end; gap: 6px; margin-top: 8px;">
                                <span style="font-size: 11px; font-weight: 700; color: #aaa; letter-spacing: 1px;">POWERED BY</span>
                                <img src="${neoLogo}" alt="NeoApp" style="height: 14px; filter: grayscale(100%) opacity(0.6);" />
                            </div>
                        </div>
                    </div>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
    };

    // ── Estado: Antes de buscar ──
    if (!hasSearched) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                <div className="avatar-md mx-auto mb-3">
                    <span className="avatar-title bg-soft-light text-muted rounded-circle font-size-24">
                        <i className="mdi mdi-filter-outline"></i>
                    </span>
                </div>
                <h6 className="fw-bold text-dark mb-1">Defina os filtros e clique em Buscar</h6>
                <p className="text-muted font-size-13 mb-0" style={{ maxWidth: 300 }}>
                    Utilize os filtros ao lado para segmentar os clientes e
                    clique em <strong>Buscar</strong> para visualizar os resultados.
                </p>
            </div>
        );
    }

    // ── Estado: Com resultados ou vazio (DataTable cuida do empty state) ──
    return (
        <React.Fragment>
            {/* Header: contagem + botões de ação */}
            <div className="d-flex align-items-center justify-content-between pb-3 flex-wrap gap-3">
                <span className="text-muted font-size-13 fw-medium">
                    <strong className="text-dark">{members.length}</strong> cliente{members.length !== 1 ? "s" : ""} encontrado{members.length !== 1 ? "s" : ""}
                </span>
                {members.length > 0 && (
                    <div className="d-flex gap-1">
                        <Button
                            color="primary"
                            size="sm"
                            className="d-flex align-items-center justify-content-center border-0 shadow-none"
                            onClick={handleExportExcel}
                            title="Exportar para Excel"
                            style={{ width: 32, height: 32, padding: 0 }}
                        >
                            <i className="mdi mdi-file-excel-outline font-size-16 text-white"></i>
                        </Button>
                        <Button
                            color="primary"
                            size="sm"
                            className="d-flex align-items-center justify-content-center border-0 shadow-none"
                            onClick={handlePrint}
                            title="Imprimir"
                            style={{ width: 32, height: 32, padding: 0 }}
                        >
                            <i className="mdi mdi-printer font-size-16 text-white"></i>
                        </Button>
                    </div>
                )}
            </div>

            {/* DataTable */}
            <DataTable
                data={members}
                columns={columns}
                keyField="id"
                noCard={true}
                emptyMessage="Nenhum cliente encontrado"
                emptySubMessage="Ajuste os filtros e tente novamente."
                isLoading={loading}
            />
        </React.Fragment>
    );
};

// ── Helpers ──

const getStatusBadge = (status) => {
    switch (status) {
        case "active":
            return { label: "Ativo", className: "bg-success bg-opacity-10 text-success" };
        case "inactive":
            return { label: "Inativo", className: "bg-secondary bg-opacity-10 text-secondary" };
        case "suspended":
            return { label: "Suspenso", className: "bg-danger bg-opacity-10 text-danger" };
        case "lead":
            return { label: "Lead", className: "bg-info bg-opacity-10 text-info" };
        default:
            return { label: status || "—", className: "bg-light text-muted" };
    }
};

const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate + "T00:00:00");
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
};

const formatPhone = (phone) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return phone;
};

export default CrmMemberList;
